import { useCallback, useMemo } from "react";
import { useConfigContext, useSubscriptionContext } from "@WahooFitness/wahoo-offline-mfe";
import { ClickTarget, EventType, SourcePage } from "@/services/Analytics/EventTypes";
import { useLogEvent } from "@/services/Analytics/useLogEvent";
import useRevenueCat from "@/components/Membership/useRevenueCat";
import { t } from "@lingui/macro";

function useSettings() {
  const {
    wahooToken,
    fitnessAppId,
    cloudConfig: { outOfTheBoxUrl, wahooMyAccountUrl },
    locale,
  } = useConfigContext();
  const { userSubscription, subscriptionIsLoading } = useSubscriptionContext();
  const { proPkg, corePkg } = useRevenueCat();
  const { logEvent } = useLogEvent();

  const subscriptionStatus = useMemo(() => {
    if (subscriptionIsLoading || !userSubscription) return "";
    if (["active", "trialing", "failed_payment"].includes(userSubscription.status || "")) {
      return t`Active`;
    }
    if (proPkg?.product.trialAvailable || corePkg?.product.trialAvailable) {
      return t`Trial Available`;
    }
    if (["cancelled", "expired"].includes(userSubscription.status || "")) {
      return t`Expired`;
    }
    return "";
  }, [subscriptionIsLoading, userSubscription, proPkg, corePkg]);

  const setupLink = useMemo(
    () => `${outOfTheBoxUrl}/guides?locale=${locale}`,
    [outOfTheBoxUrl, locale]
  );

  const accountLink = useMemo(
    () => `${wahooMyAccountUrl}/?faid=${fitnessAppId}&auth_token=${wahooToken}`,
    [wahooMyAccountUrl, fitnessAppId, wahooToken]
  );

  const logSettingsMenuEvent = useCallback(
    (clickTarget: ClickTarget) => {
      logEvent("settings_menu", {
        event_type: EventType.click,
        click_target: clickTarget,
        source_page_name: SourcePage.settings,
      });
    },
    [logEvent]
  );

  return {
    logSettingsMenuEvent,
    setupLink,
    accountLink,
    subscriptionStatus,
  };
}

export default useSettings;
